import ActionButton from '@repo/ui/action-button'
import { Column } from '@repo/ui/data-table'
import { PenIcon, PlusIcon, SettingIcon } from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import { useMutation } from '@tanstack/react-query'
import React from 'react'
import { toast } from 'sonner'

import { API } from '../../../api/apis.config'
import { createHowDidYouHearAboutUs } from '../../../api/how-did-you-hear-about-us/create-how-did-you-hear-about-us'
import { updateHowDidYouHearAboutUs } from '../../../api/how-did-you-hear-about-us/edit-how-did-you-hear-about-us'
import DynamicDataTable, { DynamicTableRef } from '../../../components/elements/dynamic-table'
import UserNavBar from '../../../components/elements/nav-bar'
import HowDidYouHearAboutUsForm from '../../../components/pages/how-did-you-hear-about-us/how-did-you-hear-about-us-form'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import { HowDidYouHearAboutUsOption } from '../../../lib/types/models/how-did-you-hear-about-us-option/how-did-you-hear-about-us-option'
import { HowDidYouHearAboutUsOptionInput } from '../../../lib/types/models/how-did-you-hear-about-us-option/how-did-you-hear-about-us-option-input'
import { modalButtonConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'

const HowDidYouHearAboutUs = () => {
  const tableRef = React.useRef<DynamicTableRef<HowDidYouHearAboutUsOption>>(null)

  const { modalState, selectedData, toggleModal, closeModel } = useModalManager<
    'edit' | 'create',
    HowDidYouHearAboutUsOption
  >(['edit', 'create'])

  const { mutateAsync: mutateEdit } = useMutation({
    mutationFn: updateHowDidYouHearAboutUs,
    onSuccess: () => {
      tableRef.current?.refetch()
      closeModel('edit')
      toast.success('Option modifiée avec succès')
    },
    onError: showAxiosError
  })

  const onSubmitEdit = (values: HowDidYouHearAboutUsOptionInput) => {
    if (!selectedData) return
    mutateEdit({
      id: selectedData.id,
      ...values
    })
  }

  const { mutateAsync } = useMutation({
    mutationFn: createHowDidYouHearAboutUs,
    onSuccess: () => {
      tableRef.current?.refetch()
      closeModel('create')
      toast.success('Option créée avec succès')
    },
    onError: showAxiosError
  })

  const onSubmit = (values: HowDidYouHearAboutUsOptionInput) => {
    mutateAsync(values)
  }

  const columns: Column<HowDidYouHearAboutUsOption>[] = [
    {
      accessorKey: 'id',
      header: 'Id',
      enableSorting: true
    },
    {
      accessorKey: 'name',
      header: 'NOM',
      enableSorting: true
    },
    {
      accessorKey: 'position',
      header: 'Position',
      enableSorting: true
    },
    {
      accessorKey: 'id',
      header: '',
      cell: (data) => (
        <ActionButton
          variant={'grey'}
          icon={PenIcon}
          onClick={() => toggleModal('edit', data.row.original)}
        />
      )
    }
  ]

  return (
    <div>
      <UserNavBar title="Comment avez-vous entendu parler de nous" icon={SettingIcon} />
      <div className="h-4" />
      <DynamicDataTable<HowDidYouHearAboutUsOption>
        ref={tableRef}
        title="Comment avez-vous entendu parler de nous"
        columns={columns}
        deleteMessage={{
          name: 'options',
          attributeName: 'name',
          deleteOne: (option) => `Êtes-vous sûr de vouloir supprimer l'option "${option.name}" ?`
        }}
        selectable
        allowSearchParam
        endpoint={API.howDidYouHearAboutUs.getAll.url}
        actions={[
          {
            type: 'global',
            icon: PlusIcon,
            label: 'Créer',
            fn: () => {
              toggleModal('create')
            }
          }
        ]}
      />

      {/* Edit */}
      <Modal
        icon={SettingIcon}
        isOpen={modalState.edit}
        title="Modifier"
        hideActions
        onCancel={() => toggleModal('edit')}
      >
        <HowDidYouHearAboutUsForm
          className={MODAL_BODY_CLASSNAME}
          defaultValues={selectedData}
          buttonsConfig={modalButtonConfig({
            onCancel: () => closeModel('edit'),
            onConfirm: onSubmitEdit
          })}
        />
      </Modal>

      {/* Create */}
      <Modal
        icon={SettingIcon}
        isOpen={modalState.create}
        title="Créer"
        hideActions
        onCancel={() => toggleModal('create')}
      >
        <HowDidYouHearAboutUsForm
          className={MODAL_BODY_CLASSNAME}
          buttonsConfig={modalButtonConfig({
            onCancel: () => closeModel('create'),
            onConfirm: onSubmit
          })}
        />
      </Modal>
    </div>
  )
}

export default HowDidYouHearAboutUs
